// services/meta.service.js
import connectionsClient, { handleApiError } from '../api/connectionsClient';

/**
 * Servicio para gestionar integraciones de Meta (WhatsApp Business, etc.)
 * Base URL: https://conn-dev.zenitailab.com
 */
class MetaService {
  /**
   * Generar URL de signup para Meta Embedded Signup OAuth flow
   * POST /meta/signup-url
   * 
   * @param {Object} params
   * @param {string} params.id - ID de la cuenta o entidad
   * @param {string|null} params.platform - Plataforma a conectar (whatsapp, facebook, instagram)
   * @returns {Promise<{success: boolean, data?: {signupUrl: string}, error?: any}>}
   */
  async generateSignupUrl({ id, platform = null, token = null }) {
    try {
      if (!id) {
        return {
          success: false,
          error: { message: 'ID is required' }
        };
      }

      const payload = {
        account_id: id,
        platform // Backend mapea esto al config_id numérico correcto
      };

      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await connectionsClient.post('/meta/signup-url', payload, config);
      
      // Manejar respuesta anidada del backend (statusCode, headers, body)
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      // Verificar si hay error en la respuesta
      if (data.statusCode && data.statusCode >= 400) {
        const errorBody = data.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
        return {
          success: false,
          error: { message: errorBody.message || 'Error generating signup URL', data: errorBody.data }
        };
      }

      // Verificar si signup_url está vacío
      if (!data.signup_url && data.data?.signup_url === '') {
        return {
          success: false,
          error: { message: data.message || 'Meta credentials not configured', data: data.data }
        };
      }

      return {
        success: true,
        data: {
          signupUrl: data.signup_url || data.data?.signup_url,
          requestId: data.request_id || data.data?.request_id,
          expiresIn: data.expires_in || data.data?.expires_in
        }
      };
    } catch (error) {
      console.error('Error generating Meta signup URL:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Procesar callback de Meta Embedded Signup
   * POST /meta/callback
   * 
   * @param {Object} params
   * @param {string} params.id - ID de la cuenta o entidad
   * @param {string} params.authorizationCode - Código de autorización de Meta
   * @param {string} params.state - Estado para verificación CSRF
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async processCallback({ id, authorizationCode, state, wabaId, phoneNumberId }) {
    try {
      if (!id || !authorizationCode || !state) {
        return {
          success: false,
          error: { message: 'ID, authorization_code and state are required' }
        };
      }

      const payload = {
        id,
        authorization_code: authorizationCode,
        state,
        ...(wabaId && { waba_id: wabaId }),
        ...(phoneNumberId && { phone_number_id: phoneNumberId })
      };

      const response = await connectionsClient.post('/meta/callback', payload);

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error processing Meta callback:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Procesar callback de Instagram Business Login
   * POST /meta/instagram/callback
   *
   * IG Business Login uses the Facebook Login flow under the hood, but the
   * backend DTO is IG-specific: only `code` + `state` (no `id` — the account
   * is resolved from the state token by the backend).
   *
   * @param {Object} params
   * @param {string} params.code - Código de autorización de Meta (FB Login)
   * @param {string} params.state - Estado para verificación CSRF (user_id[:account_id])
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async processInstagramCallback({ code, state }) {
    try {
      if (!code || !state) {
        return {
          success: false,
          error: { message: 'code and state are required' }
        };
      }

      const response = await connectionsClient.post('/meta/instagram/callback', {
        code,
        state
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error processing Instagram callback:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener detalles de una integración de Meta
   * GET /meta/integrations/{integration_id}
   * 
   * @param {string} integrationId - UUID de la integración
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getIntegration(integrationId) {
    try {
      if (!integrationId) {
        return {
          success: false,
          error: { message: 'Integration ID is required' }
        };
      }

      const response = await connectionsClient.get(`/meta/integrations/${integrationId}`);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error getting Meta integration:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar todas las integraciones de Meta para una cuenta
   * GET /meta/integrations/account/{account_id}
   * 
   * @param {string} accountId - ID de la cuenta
   * @returns {Promise<{success: boolean, data?: any[], error?: any}>}
   */
  async listIntegrations(accountId) {
    try {
      if (!accountId) {
        return {
          success: false,
          error: { message: 'Account ID is required' }
        };
      }

      const response = await connectionsClient.get(`/meta/integrations/account/${accountId}`);
      
      // Manejar respuesta anidada del backend
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }
      
      return {
        success: true,
        data: data.integrations || data.data?.integrations || data || []
      };
    } catch (error) {
      console.error('Error listing Meta integrations:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Listar integraciones de WhatsApp para una cuenta
   * GET /meta/integrations/whatsapp/list?account_id={account_id}
   * 
   * @param {string} accountId - ID de la cuenta
   * @returns {Promise<{success: boolean, data?: any[], error?: any}>}
   */
  async listWhatsAppIntegrations(accountId) {
    try {
      if (!accountId) {
        return {
          success: false,
          error: { message: 'Account ID is required' }
        };
      }

      const response = await connectionsClient.get(`/meta/integrations/whatsapp/list?account_id=${accountId}`);
      
      // Manejar respuesta anidada del backend
      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }
      
      return {
        success: true,
        data: data.integrations || data.data?.integrations || data || []
      };
    } catch (error) {
      console.error('Error listing WhatsApp integrations:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Suscribir o desuscribir un webhook de WhatsApp
   * PUT /whatsapp/webhook/subscription
   *
   * @param {Object} params
   * @param {string} params.webhookUrl - URL que recibirá los eventos de WhatsApp
   * @param {string} params.verifyToken - Token de verificación del webhook
   * @param {boolean} [params.subscribe=true] - true para suscribir, false para desuscribir
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async subscribeWebhook({ webhookUrl, verifyToken, subscribe = true }) {
    try {
      if (!webhookUrl || !verifyToken) {
        return {
          success: false,
          error: { message: 'webhookUrl and verifyToken are required' }
        };
      }

      const response = await connectionsClient.put('/whatsapp/webhook/subscription', {
        webhook_url: webhookUrl,
        verify_token: verifyToken,
        subscribe
      });

      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      if (response.data.statusCode && response.data.statusCode >= 400) {
        return {
          success: false,
          error: { message: data.message || data.error || 'Error subscribing webhook' }
        };
      }

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error subscribing WhatsApp webhook:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Suscribir o desuscribir el webhook de Instagram
   * PUT /instagram/webhook/subscription
   *
   * A diferencia de WhatsApp, el backend resuelve la cuenta de Instagram
   * del usuario autenticado automáticamente — no requiere webhookUrl ni
   * verifyToken en el body (el verify token de Instagram se configura a
   * nivel de app en el dashboard de Meta, no por cuenta).
   *
   * @param {Object} params
   * @param {boolean} [params.subscribe=true] - true para suscribir, false para desuscribir
   * @param {string} [params.webhookUrl] - URL informativa opcional a reflejar en la integración
   * @returns {Promise<{success: boolean, data?: {webhook_subscribed: boolean, instagram_username?: string}, error?: any}>}
   */
  async subscribeInstagramWebhook({ subscribe = true, webhookUrl } = {}) {
    try {
      const response = await connectionsClient.put('/instagram/webhook/subscription', {
        subscribe,
        webhook_url: webhookUrl
      });

      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error) {
      console.error('Error subscribing Instagram webhook:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Obtener el estado del teléfono en Cloud API
   * GET /whatsapp/phone/status?integration_id={id}
   *
   * @param {string} integrationId - UUID de la integración
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async getPhoneStatus(integrationId) {
    try {
      if (!integrationId) {
        return { success: false, error: { message: 'Integration ID is required' } };
      }

      const response = await connectionsClient.get(`/whatsapp/phone/status?integration_id=${integrationId}`);

      let data = response.data;
      if (data.body) {
        data = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      if (response.data.statusCode && response.data.statusCode >= 400) {
        return { success: false, error: { message: data.message || 'Error fetching phone status' } };
      }

      return { success: true, data: data.data || data };
    } catch (error) {
      console.error('Error fetching WhatsApp phone status:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * Eliminar una integración de Meta
   * DELETE /meta/integrations/{integration_id}
   * 
   * @param {string} integrationId - UUID de la integración
   * @returns {Promise<{success: boolean, data?: any, error?: any}>}
   */
  async deleteIntegration(integrationId) {
    try {
      if (!integrationId) {
        return {
          success: false,
          error: { message: 'Integration ID is required' }
        };
      }

      const response = await connectionsClient.delete(`/meta/integrations/${integrationId}`);
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error deleting Meta integration:', error);
      return { success: false, error: handleApiError(error) };
    }
  }

  /**
   * GET /meta/integrations/account/{account_id}
   * Returns the most recent active integration for the given account and platform.
   */
  async getLatestIntegration({ accountId, platform, token = null }) {
    try {
      const config = { params: { only_active: true } };
      if (token) config.headers = { Authorization: `Bearer ${token}` };
      const response = await connectionsClient.get(`/meta/integrations/account/${accountId}`, config);
      // API Gateway Lambda proxy returns body as a JSON string inside the response
      const raw = response.data;
      const inner = typeof raw?.body === 'string' ? JSON.parse(raw.body) : raw;
      const integrations = inner?.data?.integrations || inner?.integrations || [];
      const match = integrations
        .filter((i) => !platform || i.platforms_enabled?.[platform] === true)
        .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
      return { success: true, integration_id: match?.id || null };
    } catch (error) {
      console.error('Error fetching latest integration:', error);
      return { success: false, integration_id: null };
    }
  }
}

export default new MetaService();
